import React, { useEffect, useState, memo, useContext, useRef, useMemo, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Route, useHistory, useLocation } from 'react-router-dom'
import { useEffectOnce } from 'react-use'
import { Tabs, Tab, LAYOUT } from 'transfix-ui/components/DeprecatedTabs'
import { INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import { ModalBase, MODAL_TYPES } from 'transfix-ui/components/Modals'
import { TAG_STYLE, TAG_SIZE, Tag } from 'transfix-ui/components/Tag'
import { Box, makeStyles } from 'transfix-ui/core'
import { calcGrid } from 'transfix-ui/theme'
import { brandColors } from 'transfix-ui/theme/colors'
import { StringParam } from 'use-query-params'
import { useChatWidgetContext } from 'utils/context/ChatWidgetContext'
import { FeatureFlagsContext } from 'utils/context/FeatureFlagsContext'
import { useMarketplaceContext } from 'utils/context/MarketplaceContext'
import { useGetShipmentLocationsData } from 'utils/hooks/useGetShipmentLocationsData'
import { ShareLoadModal } from 'apps/fleet-planner/components/share-load/ShareLoadModal'
import { ShareLoadToastWrapper } from 'apps/fleet-planner/components/share-load/ShareLoadToastWrapper'
import { LOAD_TABS } from 'apps/fleet-planner/constants'
import { ShareLoadProvider } from 'apps/fleet-planner/context/ShareLoadContext'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { useTodosApi } from 'apps/fleet-planner/hooks/useTodosApi'
import { obscurePostDeliveryStatus } from 'apps/fleet-planner/pages/loads/utils'
import { ILoadStatusOption, ILoad } from 'apps/fleet-planner/types'
import { fetchDispatchers } from 'apps/fleet-planner/utils/actions/dispatchers'
import { fetchDrivers } from 'apps/fleet-planner/utils/actions/drivers'
import { fetchEquipment } from 'apps/fleet-planner/utils/actions/equipment'
import {
  fetchLoadDetails,
  updateLoadStatus,
  updateLoad,
} from 'apps/fleet-planner/utils/actions/loadDetails'
import { getTotalDocumentsCount } from 'apps/fleet-planner/utils/api'
import { getTrackingInfo } from 'apps/fleet-planner/utils/marketplaceHelpers'
import { scrollToField } from 'apps/fleet-planner/utils/scrollToField'
import { loadDetailsSelector } from 'apps/fleet-planner/utils/selectors/load'
import { modalInfoSelector } from 'apps/fleet-planner/utils/selectors/modals'
import { useQueryParams } from 'utils/hooks'
import { DISPLAY } from 'constants/display'
import { BookableShipmentSavedSearchPostTruckReminderModal } from '../bookable-shipment-saved-search/alert/BookableShipmentSavedSearchPostTruckReminderModal'
import { TruckReminderModalDeprecated } from '../bookable-shipment-saved-search/alert/TruckReminderModalDeprecated'
import { DeleteLoadModal } from '../modals/DeleteLoadModal'
import { ShareLoadViaSmsModal } from '../share-load/modals/shareLoadViaSms/ShareLoadViaSmsModal'
import { SmsPromptSettingsModal } from '../share-load/modals/smsPromptSettings/SmsPromptSettingsModal'
import { CheckinCheckOutTimesModal } from './CheckinCheckoutTimesModal'
import { LoadDetailsAlerts } from './LoadDetailsAlerts'
import { LoadDetailsDocuments } from './LoadDetailsDocuments'
import { LoadDetailsELDSection } from './LoadDetailsELDSection'
import { LoadDetailsHeader } from './LoadDetailsHeader'
import { LoadDetailsInlineEdit } from './LoadDetailsInlineEdit'
import { LoadDetailsReloadMessage } from './LoadDetailsReloadsMessage'
import { LoadNotesInlineEdit } from './LoadNotesInlineEdit'
import { LoadStatus } from './LoadStatus'
import { PickupEtaModal } from './PickupEtaModal'
import { LoadActivity } from './activity/LoadActivity'
import { LoadDetailsInvoice } from './quickbooks/LoadDetailsInvoice'
import { Stops } from './stops/Stops'
import { StopsSummary } from './stops/StopsSummary'

const EMPTY_OBJECT = {}

interface ILoadDetailsModalProps {
  id?: string
  onClose: () => void
  refetchOptions?: boolean
  /**
   * @default '/loads/:id'
   */
  path?: string
}

interface ILoadDetailsModalTitleProps {
  externalId?: string
  isSample?: boolean
}

export const LoadDetailsModalTitle = memo<ILoadDetailsModalTitleProps>(
  ({ externalId, isSample }) => {
    const { trackEvent } = useFMSAnalytics()
    if (isSample) {
      trackEvent('Onboarding', 'VIEWED_SAMPLE_LOAD')
    }
    return (
      <div className='flex flex-row items-center'>
        <div className='mr2'>{`Load # ${externalId || DISPLAY.empty}`}</div>
        {isSample && (
          <Tag
            label='Sample Load'
            size={TAG_SIZE.small}
            tagStyle={TAG_STYLE.success}
            dataTest='load-details-sample-tag'
          />
        )}
      </div>
    )
  }
)

export const useLoadDetailsModalStyles = makeStyles(theme => ({
  content: {
    width: '100%',

    [theme.breakpoints.up('md')]: {
      height: '100%',
      width: calcGrid(7),
      overflow: 'scroll',
    },
  },
  contentInner: {
    padding: 0,
    [theme.breakpoints.up('md')]: {
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3),
      paddingBottom: theme.spacing(10),
    },
  },
  sidebar: {
    width: '100%',

    [theme.breakpoints.up('md')]: {
      height: '100%',
      width: calcGrid(5),
      overflowY: 'scroll',
      overflowX: 'hidden',
      borderLeft: `1px solid ${brandColors.coolGray4}`,
      paddingBottom: theme.spacing(10),
    },
  },
  sidebarInner: {
    padding: 0,

    [theme.breakpoints.up('md')]: {
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3),
    },
  },
  stopSummaryWrapper: {
    // removes extra padding on mobile for stop summary
    marginLeft: `-${theme.spacing(2)}`,
    marginRight: `-${theme.spacing(2)}`,
    marginTop: `-${theme.spacing(2)}`,

    [theme.breakpoints.up('md')]: {
      marginTop: 0,
      marginLeft: 0,
      marginRight: 0,
    },
  },
}))

export const LoadDetailsModal = memo<ILoadDetailsModalProps>(
  ({ id, onClose, path = '/loads/:id', refetchOptions }) => {
    const { error, load, loading, permissions } = useSelector(loadDetailsSelector)
    const { getFlag } = useContext(FeatureFlagsContext)
    const loadComments = getFlag('loadComments')
    const { enabled: isFmsReloadsModalEnabled } = getFlag('fmsReloadsModal')
    const { enabled: isAddReloadEnabled } = getFlag('fpAddReloads')
    const { enabled: fpQuickbooksIntegrationEnabled = false } = getFlag('fpQuickbooksIntegration')
    const { enabled: loadActivityEnabled } = getFlag('loadDetailsActivity')
    const { enabled: isFpCarrierPortalEnabled } = getFlag('fpCarrierPortal')
    const { enabled: isFmsELDTrackingEnabled } = getFlag('fmsELDTracking')

    const dispatch = useDispatch()
    const { update } = useChatWidgetContext()
    const { pathname = '' } = useLocation()
    const history = useHistory()

    const { actionInProgress: saving, error: modalError } = useSelector(modalInfoSelector)

    const updateStatusHandler = useCallback(
      ({ currentStopSequence, status: newStatus }: ILoadStatusOption) => {
        dispatch(updateLoadStatus(load, newStatus, currentStopSequence))
      },
      [dispatch, load]
    )

    const closeShareModalHandler = useCallback(() => {
      history.push(pathname.replace(/(\/share)/, ''))
    }, [history, pathname])

    const closeShareLoadModalHandler = useCallback(() => {
      history.push(pathname.replace(/(\/share-load-via-sms)/, ''))
    }, [history, pathname])

    const closeSmsSettingsModalHandler = useCallback(() => {
      history.push(pathname.replace(/(\/sms-prompt-settings)/, ''))
    }, [history, pathname])

    const closeCheckinCheckoutTimesModalHandler = useCallback(() => {
      history.push(pathname.replace(/(\/update-status)/, ''))
    }, [history, pathname])

    const closePickupEtaModalHandler = useCallback(() => {
      history.push(pathname.replace(/(\/update-eta)/, ''))
    }, [history, pathname])

    const [{ focusInput }] = useQueryParams({
      focusInput: StringParam,
    })

    const [inlineEditField, setInlineEditField] = useState<string | undefined>(focusInput)
    const [currentTab, setCurrentTab] = useState(LOAD_TABS.loadDetails)

    const { findTodoByExternalId, getTodos, isLoading: isTodosLoading } = useTodosApi()

    useEffectOnce(() => {
      if (!isTodosLoading) {
        getTodos()
      }
    })

    useEffect(() => {
      if (id) {
        setInlineEditField(focusInput)
        setCurrentTab(LOAD_TABS.loadDetails)
        dispatch(fetchLoadDetails(id))
        // Forces Intercom to refresh so that product tour can transition properly
        update()
      }
    }, [dispatch, id, focusInput])

    useEffectOnce(() => {
      dispatch(fetchDrivers())
      dispatch(fetchDispatchers())
      dispatch(fetchEquipment())
    })

    const {
      id: loadId,
      isExternal,
      notes,
      motorCarrier,
      dispatcher,
      truck,
      trailer,
      status: loadStatus,
      drivers,
      currentStopSequence,
      externalId,
      isSample,
      stops,
      rate,
      miles,
      trailerType,
      trailerLength,
      customer,
      customerId,
      customerContact,
      customerContactId,
      temperatureSetting,
      externalEntities,
      instructionsFromCustomer,
      customerNotes,
      securingOptions,
      subscribers = [],
      settings,
      quickbooksInvoiceId,
      trackingMethod,
    } = load || {}

    const status = obscurePostDeliveryStatus(loadStatus, isExternal)

    const { data, refetch } = useGetShipmentLocationsData({
      shipmentId: `${id}`,
      lazy: true,
      debounce: true,
    })
    const { recentLocations = [], lastLocationIsForCurrentShipment, eta } = data || {}

    /**
     * When the shipment is an external load, fetch location data for ELD tracking
     */
    useEffect(() => {
      if (id && isExternal && isFmsELDTrackingEnabled && status) {
        refetch({
          pathParams: {
            shipmentId: id,
          },
        })
      }
    }, [id, isExternal, isFmsELDTrackingEnabled, refetch, status])

    const currentShipmentTodos = findTodoByExternalId(externalId || '')

    const tabSwitchHandler = (newTab: LOAD_TABS) => {
      setCurrentTab(newTab)
    }

    const {
      getMarketplaceLoad,
      shipmentDetails,
      updateError: externalUpdateError,
      updateTractorVin,
    } = useMarketplaceContext()

    const updateLoadHandler = useCallback(
      (changes: Partial<ILoad>) => {
        if (id) {
          dispatch(
            updateLoad({
              ...load,
              ...changes,
            })
          )
        }
      },
      [dispatch, id, load]
    )

    const errors = error || externalUpdateError
    const { carrierPayment, info, shipperInsuranceRequirement, stops: marketplaceStops } =
      shipmentDetails || {}
    const {
      bills,
      carrierRate,
      carrierTotalAccessorialRate,
      paymentTerms,
      quickPayEnabled,
      total,
    } = carrierPayment || {}
    const { vin } = info || {}

    const updateTractorVinHandler = useCallback(
      (tractorVin: string) => {
        if (loadId && isExternal && externalId) {
          updateTractorVin(loadId, externalId, tractorVin)
        }
      },
      [externalId, isExternal, loadId, updateTractorVin]
    )

    // Fetch shipment details if load has been synced with Transfix and if feature flag is enabled.
    useEffect(() => {
      if (isExternal && externalId && isFpCarrierPortalEnabled) {
        getMarketplaceLoad(externalId)
      }
    }, [externalId, getMarketplaceLoad, isExternal, isFpCarrierPortalEnabled])

    /**
     * This method brings the modal into focus so we can tab and scroll through the content
     * using keyboard buttons.
     */
    const modalRef = useRef<HTMLInputElement>(null)
    useEffect(() => {
      if (modalRef.current) modalRef.current.focus()
    }, [])

    const totalAttachmentsCount = useMemo(
      () =>
        getTotalDocumentsCount({
          fpLoad: load,
          mpShipment: shipmentDetails,
          isFpCarrierPortalEnabled,
        }),
      [isFpCarrierPortalEnabled, load, shipmentDetails]
    )

    const setAndScroll = useCallback((name?: string, className?: string) => {
      scrollToField(className)
      setInlineEditField(name)
    }, [])
    const classes = useLoadDetailsModalStyles()

    const { isTrackableStatus } = getTrackingInfo({
      mpShipment: shipmentDetails,
      fpLoad: load,
    })

    const isTrackingVisible = [isFpCarrierPortalEnabled, id, isExternal].every(s => !!s)

    return (
      <ModalBase
        title={<LoadDetailsModalTitle externalId={externalId} isSample={isSample} />}
        size={10}
        modalType={MODAL_TYPES.form}
        open={Boolean(id)}
        onClose={onClose}
        dataTest='modal-load-details'
        className='fp-loads-modal fp-load-details-modal'
        scrollableHeight
        header={<LoadDetailsHeader load={load} isExternal={isExternal} />}
        inlineMessageProps={
          !errors
            ? undefined
            : {
                dataTest: 'modal-edit-load-error',
                message: errors,
                type: INLINE_MESSAGE_VARIANTS.ALERT,
              }
        }
        isClose>
        <Box className={classes.content}>
          <Box className={classes.contentInner}>
            <LoadDetailsAlerts
              onTodoClick={setAndScroll}
              onPODUpload={tabSwitchHandler}
              currentShipmentTodos={currentShipmentTodos}
            />
            <Box className={classes.stopSummaryWrapper}>
              <StopsSummary stops={stops} />
            </Box>
            <Tabs value={currentTab} onChange={tabSwitchHandler} layout={LAYOUT.scrollable}>
              <Tab label={LOAD_TABS.loadDetails}>
                {isAddReloadEnabled && <LoadDetailsReloadMessage stops={stops} />}
                {fpQuickbooksIntegrationEnabled && (
                  <LoadDetailsInvoice
                    status={status}
                    customer={customer}
                    id={loadId}
                    quickbooksInvoiceId={quickbooksInvoiceId}
                    rate={rate}
                  />
                )}
                <LoadDetailsInlineEdit
                  carrierTotalAccessorialRate={carrierTotalAccessorialRate}
                  isQuickpayEnabled={quickPayEnabled}
                  paymentTerms={paymentTerms}
                  carrierRate={carrierRate}
                  totalRate={total}
                  securingOptions={securingOptions}
                  isExternal={isExternal}
                  customerNotes={customerNotes}
                  permissions={permissions}
                  externalId={externalId}
                  rate={rate}
                  miles={miles}
                  trailerType={trailerType}
                  trailerLength={trailerLength}
                  customer={customer}
                  customerId={customerId}
                  customerContact={customerContact}
                  customerContactId={customerContactId}
                  temperatureSetting={temperatureSetting}
                  onEditModeRequested={setInlineEditField}
                  currentEditField={inlineEditField}
                  onUpdate={updateLoadHandler}
                />
                <Stops
                  status={status}
                  marketplaceStops={marketplaceStops}
                  isExternal={isExternal}
                  permissions={permissions}
                  loadId={loadId}
                  loading={loading}
                  error={error}
                  stops={stops}
                  onEditModeRequested={setInlineEditField}
                  currentEditField={inlineEditField}
                  updateLoad={updateLoadHandler}
                />
                <LoadNotesInlineEdit
                  shipperInsuranceRequirement={shipperInsuranceRequirement}
                  instructionsFromCustomer={instructionsFromCustomer}
                  isExternal={isExternal}
                  onEditModeRequested={setInlineEditField}
                  currentEditField={inlineEditField}
                  notesText={notes}
                  updateLoad={updateLoadHandler}
                />
              </Tab>
              {loadComments?.enabled && (
                <Tab label={LOAD_TABS.comments}>
                  <div />
                </Tab>
              )}
              {loadActivityEnabled && (
                <Tab label={LOAD_TABS.activity}>
                  <LoadActivity loadId={load.id} loadStops={stops} isExternal={isExternal} />
                </Tab>
              )}
              <Tab
                dataTest='attachments-tab'
                label={LOAD_TABS.documents}
                count={totalAttachmentsCount}>
                <LoadDetailsDocuments />
              </Tab>
            </Tabs>
          </Box>
        </Box>
        <Box className={[classes.sidebar, 'sidebar-wrapper'].join(' ')}>
          {isTrackingVisible && isFmsELDTrackingEnabled && (
            <LoadDetailsELDSection
              shipmentId={id}
              trackingMethod={trackingMethod}
              recentLocations={recentLocations}
              eta={eta}
              isTrackableStatus={isTrackableStatus}
              status={loadStatus}
              truck={truck}
              drivers={drivers}
              carrier={motorCarrier}
              isCurrentShipment={lastLocationIsForCurrentShipment}
              isExternal={isExternal}
              path={path}
            />
          )}
          <Box className={classes.sidebarInner}>
            <LoadStatus
              onTractorVinUpdate={updateTractorVinHandler}
              tractorVinNumber={vin}
              permissions={permissions}
              isExternal={isExternal}
              externalId={externalId}
              externalEntities={externalEntities}
              updateLoad={updateLoadHandler}
              onStatusUpdate={updateStatusHandler}
              drivers={drivers}
              dispatcher={dispatcher}
              carrier={motorCarrier}
              truck={truck || EMPTY_OBJECT}
              trailer={trailer || EMPTY_OBJECT}
              status={status}
              stops={stops}
              currentStopSequence={currentStopSequence}
              inlineEditField={inlineEditField}
              setInlineEditField={setInlineEditField}
              refetchOptions={refetchOptions}
              id={id}
              isStatusUpdating={loading} // Proxy for now, we can revisit later
              subscribers={subscribers}
              settings={settings}
              customerContact={customerContact}
              setAndScrollToField={setAndScroll}
              currentShipmentTodos={currentShipmentTodos}
              bills={bills}
            />
          </Box>
        </Box>
        <Route exact path={`${path}/delete`}>
          <DeleteLoadModal id={id} showDeleteModal saving={saving} error={modalError} />
        </Route>
        <Route exact path={`${path}/post-truck`}>
          {isFmsReloadsModalEnabled ? (
            <BookableShipmentSavedSearchPostTruckReminderModal stops={stops} />
          ) : (
            <TruckReminderModalDeprecated />
          )}
        </Route>
        <Route exact path={`${path}/share`}>
          <ShareLoadProvider>
            <ShareLoadToastWrapper />
            <ShareLoadModal loadId={id} isOpen onClose={closeShareModalHandler} />
          </ShareLoadProvider>
        </Route>
        <Route exact path={`${path}/share-load-via-sms`}>
          <ShareLoadViaSmsModal open onClose={closeShareLoadModalHandler} />
        </Route>
        <Route exact path={`${path}/sms-prompt-settings`}>
          <SmsPromptSettingsModal open onClose={closeSmsSettingsModalHandler} />
        </Route>
        <Route exact path={`${path}/update-status`}>
          <CheckinCheckOutTimesModal
            load={load}
            isOpen
            onClose={closeCheckinCheckoutTimesModalHandler}
          />
        </Route>
        <Route exact path={`${path}/update-eta`}>
          <PickupEtaModal load={load} isOpen onClose={closePickupEtaModalHandler} />
        </Route>
      </ModalBase>
    )
  }
)
